import { EntryKey, useMutation, useQueryCache } from "@pinia/colada";
import { RecipientResourceType, ResourceIdentity, useApi } from "@soft-db/monitoring-api-client";
import { alertsKeys, useAlert } from "./alert-query";
import { useCurrentUser } from "./current-user";
import { reportsKeys, useReport } from "./report-query";
import { sitesKeys, useSite } from "./site-query";
import { stationsKeys, useStation } from "./station-query";

function useInvalidateResources() {
	const cache = useQueryCache();

	return (type: RecipientResourceType) => {
		let key: EntryKey;

		switch (type) {
			case "station":
				key = stationsKeys.root;
				break;

			case "site":
				key = sitesKeys.root;
				break;

			case "alert":
				key = alertsKeys.root;
				break;

			case "report":
				key = reportsKeys.root;
				break;
		}

		cache.invalidateQueries({ key });
	};
}

export function useSubscribeTo() {
	const api = useApi().recipients();

	const { currentUser } = useCurrentUser();

	const invalidateResources = useInvalidateResources();

	return useMutation({
		mutation: async ({ type, id }: ResourceIdentity) => {
			if (currentUser.value == null) {
				throw new Error("user not authorized");
			}

			return api.currentUserSubscribe({ type, id });
		},
		onSuccess: (_, { type }) => {
			invalidateResources(type);
		},
	});
}

export function useUnsubscribeTo() {
	const api = useApi().recipients();

	const { currentUser } = useCurrentUser();

	const invalidateResources = useInvalidateResources();

	return useMutation({
		mutation: async ({ type, id }: ResourceIdentity) => {
			if (currentUser.value == null) {
				throw new Error("user not authorized");
			}

			return api.currentUserUnsubscribe({ type, id });
		},
		onSuccess: (_, { type }) => {
			invalidateResources(type);
		},
	});
}

export function useSubscribtionToggle(resource: MaybeRefOrGetter<ResourceIdentity>) {
	let isSubscribed: ComputedRef<boolean>;

	switch (toValue(resource).type) {
		case "station": {
			const { station } = useStation(() => toValue(resource).id);
			isSubscribed = computed(() => station.value?.isSubscribed || false);
			break;
		}

		case "site": {
			const { site } = useSite(() => toValue(resource).id);
			isSubscribed = computed(() => site.value?.isSubscribed || false);
			break;
		}

		case "alert": {
			const { alert } = useAlert(() => toValue(resource).id);
			isSubscribed = computed(() => alert.value?.isSubscribed || false);
			break;
		}

		case "report": {
			const { report } = useReport(() => toValue(resource).id);
			isSubscribed = computed(() => report.value?.isSubscribed || false);
		}
	}

	const subscribe = useSubscribeTo();
	const unsubscribe = useUnsubscribeTo();

	async function toggle() {
		if (isSubscribed.value) {
			await unsubscribe.mutateAsync(toValue(resource));
		} else {
			await subscribe.mutateAsync(toValue(resource));
		}
	}

	return {
		isSubscribed,
		toggle,
	};
}
